/**
 * Chain-local week arithmetic shared by the Recognition OKRs dashboard and the review tool.
 *
 * The api-dl Recognition endpoints reject a `week_start` that is not a Monday with a 400, so every
 * surface that lets a reader pick a plain date has to snap it before asking for a week. Weeks are
 * chain-local on the backend, but the boundary itself is a calendar Monday, so the arithmetic here
 * runs in UTC and cannot drift a day in a negative-offset time zone.
 */

/**
 * Parse a `YYYY-MM-DD` calendar date as UTC midnight, or `null` when it is not one.
 *
 * `Date.UTC` normalizes out-of-range components rather than failing, so `2026-02-30` would become
 * March 2 and quietly pass as a date. The parsed value is therefore re-formatted and compared with
 * the input: only a date that survives the round trip is a real calendar day.
 */
export function parseYmd(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return null;
  return toYmd(date) === value ? date : null;
}

export function toYmd(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Snap a `YYYY-MM-DD` date to the Monday that starts its week; `null` when it is not a date. */
export function mondayOf(value: string): string | null {
  const date = parseYmd(value);
  if (!date) return null;
  const mondayIndexedWeekday = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - mondayIndexedWeekday);
  return toYmd(date);
}

export function shiftWeekStart(weekStart: string, weeks: number): string | null {
  const date = parseYmd(weekStart);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() + weeks * 7);
  return toYmd(date);
}

/** The `count` week starts ending at (and including) `latestWeekStart`, oldest first. */
export function recentWeekStarts(latestWeekStart: string, count: number): string[] {
  const weeks: string[] = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const weekStart = shiftWeekStart(latestWeekStart, -offset);
    if (weekStart) weeks.push(weekStart);
  }
  return weeks;
}

/** Last calendar day of the week `weekStart` begins, as `YYYY-MM-DD`. */
export function weekEnd(weekStart: string): string | null {
  const date = parseYmd(weekStart);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() + 6);
  return toYmd(date);
}

/**
 * Whether `capturedDate` is one of the seven days of the week starting `weekStart`.
 *
 * api-dl answers a `captured_date` outside the requested week with a 400 that fails the WHOLE queue
 * request, so this is a precondition the client has to check rather than a filter the server
 * quietly drops. An unparseable date, or a week that could not be resolved, is not in the week.
 *
 * @param capturedDate Chain-local `YYYY-MM-DD` the reviewer asked for.
 * @param weekStart The Monday the queue request will resolve to, or `null` when it is unknown.
 * @returns True when the date can be sent as-is.
 */
export function isDateInWeek(capturedDate: string, weekStart: string | null): boolean {
  if (!weekStart) return false;
  const monday = mondayOf(weekStart);
  const end = monday && weekEnd(monday);
  if (!monday || !end) return false;
  return parseYmd(capturedDate) !== null && capturedDate >= monday && capturedDate <= end;
}

export function lastCompletedWeekStart(now: Date = new Date()): string | null {
  const thisWeek = mondayOf(toYmd(now));
  return thisWeek ? shiftWeekStart(thisWeek, -1) : null;
}
